/**
 * FirmwareDevicesSection — device-level BIOS table (shared component).
 *
 * Extracted from FirmwareReportTab (2026-08) so the device view can be
 * embedded BOTH in Reports → Firmware (expanded, KPI-tile-filterable) and
 * at the bottom of OS Patch → Discover → Firmware as a collapsed section
 * (user request: keep the model-based view, restore the device list).
 *
 * Data: GET /firmware/devices (+ /firmware/devices/filter-options).
 * Collapsed instances fetch nothing until first expanded.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Cpu, RefreshCw, Filter, X, Download, Search, ExternalLink,
  AlertTriangle, Rocket, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { usePermissions } from '@/contexts/PermissionsContext';

import { FirmwareStatusChip, PaginationControls } from '../OSPatchPages';
import { FirmwareDeploymentWizard } from './FirmwareDeploymentsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/os-patch`;

// CSV-quote a single cell. Wraps in quotes and escapes embedded quotes when
// the cell contains a comma, quote or newline. Mirrors RFC 4180.
export const _csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

/**
 * Searchable multi-select dropdown. Renders a chip-styled trigger button;
 * opening it shows a search box + checkboxed list of values. Stable
 * controlled component — caller owns the array of selected values.
 */
export function MultiSelectFilter({
  label, options, selectedValues, onChange, testIdPrefix, placeholder,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [query, options]);
  const toggle = (v) => {
    const has = selectedValues.includes(v);
    onChange(has ? selectedValues.filter((x) => x !== v) : [...selectedValues, v]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); setQuery(''); };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.15)] hover:bg-[rgba(229,239,250,0.15)] text-white"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <Filter className="w-3.5 h-3.5 text-cyan-400" />
        <span>{label}</span>
        {selectedValues.length > 0 && (
          <>
            <span className="px-1.5 py-0.5 rounded-full bg-cyan-500/30 text-cyan-100 text-xs">
              {selectedValues.length}
            </span>
            <X
              className="w-3.5 h-3.5 text-white/60 hover:text-white"
              onClick={clear}
              data-testid={`${testIdPrefix}-clear`}
            />
          </>
        )}
      </button>
      {open && (
        <div
          className="alice-menu absolute z-30 mt-1 w-72 max-h-80 overflow-hidden flex flex-col rounded-lg border border-[rgba(229,239,250,0.15)] bg-[#0f1e33]/95 backdrop-blur-md shadow-xl"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          <div className="p-2 border-b border-[rgba(229,239,250,0.1)]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/50" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={placeholder || 'Search…'}
                className="w-full pl-7 pr-2 py-1.5 text-sm bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.15)] rounded-md text-white placeholder-white/40 focus:outline-none focus:border-cyan-400"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-white/50">No matches</div>
            ) : filtered.map((opt) => {
              const checked = selectedValues.includes(opt);
              return (
                <label
                  key={opt}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-white hover:bg-[rgba(229,239,250,0.1)] cursor-pointer"
                  data-testid={`${testIdPrefix}-option-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt)}
                    className="accent-cyan-500"
                  />
                  <span className="flex-1 truncate" title={opt}>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


export default function FirmwareDevicesSection({
  statusFilter = null,
  summary = null,
  refreshKey = null,
  collapsible = false,
  defaultCollapsed = false,
  // October 2026: Discover > Devices links here for one device
  // (`?tab=firmware&device_search=<name>`). When set, the section opens,
  // filters to that name and scrolls into view; `onClearDeviceSearch` drops it.
  deviceSearch = null,
  onClearDeviceSearch = null,
}) {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed && !deviceSearch);
  const sectionRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ manufacturers: [], models: [] });
  const [selectedMfrs, setSelectedMfrs] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [devicesTotal, setDevicesTotal] = useState(null);
  const [deployWizardSelection, setDeployWizardSelection] = useState(null);
  // Read-only OS Patch tenants: hide per-device Package & Deploy.
  const { canWriteOSPatch } = usePermissions();

  // Distinct list of Model labels filtered down to the chosen manufacturers
  // (so picking "LENOVO" narrows the Model dropdown to only Lenovo families).
  const modelOptions = useMemo(() => {
    const rows = filterOptions.models || [];
    if (selectedMfrs.length === 0) return rows.map((r) => r.model);
    const mfrSet = new Set(selectedMfrs.map((m) => m.toLowerCase()));
    return rows
      .filter((r) => mfrSet.has((r.manufacturer || '').toLowerCase()))
      .map((r) => r.model);
  }, [filterOptions, selectedMfrs]);

  // Reset to page 1 whenever the caller's KPI status filter changes.
  useEffect(() => { setPage(1); }, [statusFilter, deviceSearch]);

  // A device link opens the section and brings it into view.
  useEffect(() => {
    if (!deviceSearch) return;
    setCollapsed(false);
    const t = setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(t);
  }, [deviceSearch]);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter) params.firmware_status = statusFilter;
      if (selectedMfrs.length > 0) params.manufacturer = selectedMfrs.join(',');
      if (selectedModels.length > 0) params.model = selectedModels.join(',');
      // The backend treats `search` as a regex over name, user and serial;
      // escape it so a device name with brackets or dots matches literally.
      if (deviceSearch) params.search = `^${deviceSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;
      const r = await axios.get(`${API}/firmware/devices`, { params });
      setDevices(r.data?.devices || []);
      setDevicesTotal(r.data?.total ?? null);
    } catch (err) {
      console.error('Firmware devices load failed:', err);
      toast.error('Failed to load firmware devices');
    } finally {
      setLoading(false);
    }
  };

  // Load on filter/page change — collapsed instances fetch nothing.
  // eslint-disable-next-line
  useEffect(() => { if (!collapsed) loadDevices(); },
    [statusFilter, page, pageSize, selectedMfrs, selectedModels, collapsed, refreshKey, deviceSearch]);

  // Distinct manufacturer + model lists (refreshes when a catalogue sync
  // changes the resolved family mappings via refreshKey).
  useEffect(() => {
    if (collapsed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/firmware/devices/filter-options`);
        if (!cancelled) {
          setFilterOptions({
            manufacturers: r.data?.manufacturers || [],
            models: r.data?.models || [],
          });
        }
      } catch (err) {
        console.warn('Failed to load firmware filter options:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey, collapsed]);

  // CSV export — streams every matching device (no pagination) at the
  // currently-active filter set. Backend caps page_size at 500 so we loop.
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const headers = [
        'Device Name', 'User', 'Manufacturer', 'Model', 'Serial Number',
        'OS Version', 'BIOS Version', 'Latest Available', 'Status',
        'TPM', 'BIOS Download URL', 'Last Sync',
      ];
      const lines = [headers.map(_csvCell).join(',')];
      let p = 1;
      const ps = 500;
      const maxRows = 50000;
      while (lines.length - 1 < maxRows) {
        const params = { page: p, page_size: ps };
        if (statusFilter) params.firmware_status = statusFilter;
        if (selectedMfrs.length > 0) params.manufacturer = selectedMfrs.join(',');
        if (selectedModels.length > 0) params.model = selectedModels.join(',');
        // eslint-disable-next-line no-await-in-loop
        const r = await axios.get(`${API}/firmware/devices`, { params });
        const rows = r.data?.devices || [];
        if (rows.length === 0) break;
        rows.forEach((d) => {
          lines.push([
            d.device_name, d.user_name, d.manufacturer,
            d.model_family || d.model, d.serial_number, d.os_version,
            d.bios_version, d.latest_bios_version, d.firmware_status,
            d.tpm_version, d.latest_bios_download_url, d.last_sync,
          ].map(_csvCell).join(','));
        });
        const total = r.data?.total || 0;
        if (rows.length < ps || p * ps >= total) break;
        p += 1;
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `firmware-devices-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${lines.length - 1} devices to CSV`);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export firmware CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card
      ref={sectionRef}
      className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]"
      data-testid="firmware-devices-section"
    >
      <CardHeader
        className={`border-b border-[rgba(229,239,250,0.1)] ${collapsible ? 'cursor-pointer select-none hover:bg-[rgba(229,239,250,0.06)] transition-colors' : ''}`}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
        data-testid="firmware-devices-section-header"
      >
        <CardTitle className="text-white flex items-center gap-2">
          <Cpu className="w-5 h-5 text-cyan-400" />
          Devices
          {loading && <RefreshCw className="w-4 h-4 text-white/60 animate-spin ml-2" />}
          {collapsible && (
            <span className="ml-auto flex items-center gap-2 text-sm text-white/60 font-normal">
              {collapsed ? 'Show device-level BIOS view' : 'Hide'}
              {collapsed
                ? <ChevronRight className="w-4 h-4" data-testid="firmware-devices-chevron-collapsed" />
                : <ChevronDown className="w-4 h-4" data-testid="firmware-devices-chevron-open" />}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      {!collapsed && (
        <CardContent className="p-0">
          {/* Filter row */}
          <div
            className="flex items-center gap-2 px-4 py-3 border-b border-[rgba(229,239,250,0.1)] flex-wrap"
            data-testid="firmware-devices-filter-row"
          >
            {deviceSearch && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-200 border border-cyan-400/30"
                data-testid="firmware-devices-device-filter"
              >
                Device: {deviceSearch}
                {onClearDeviceSearch && (
                  <button type="button" onClick={onClearDeviceSearch} className="hover:text-white" title="Show all devices">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            )}
            <MultiSelectFilter
              label="Manufacturer"
              options={filterOptions.manufacturers || []}
              selectedValues={selectedMfrs}
              onChange={(vals) => {
                setSelectedMfrs(vals);
                // Drop any selected models no longer offered for the
                // new manufacturer set.
                if (selectedModels.length > 0) {
                  const rows = filterOptions.models || [];
                  let allowed;
                  if (vals.length === 0) {
                    allowed = new Set(rows.map((r) => r.model));
                  } else {
                    const mfrSet = new Set(vals.map((m) => m.toLowerCase()));
                    allowed = new Set(
                      rows
                        .filter((r) => mfrSet.has((r.manufacturer || '').toLowerCase()))
                        .map((r) => r.model),
                    );
                  }
                  const kept = selectedModels.filter((m) => allowed.has(m));
                  if (kept.length !== selectedModels.length) setSelectedModels(kept);
                }
                setPage(1);
              }}
              testIdPrefix="firmware-mfr-filter"
              placeholder="Search manufacturers…"
            />
            <MultiSelectFilter
              label="Model"
              options={modelOptions}
              selectedValues={selectedModels}
              onChange={(vals) => { setSelectedModels(vals); setPage(1); }}
              testIdPrefix="firmware-model-filter"
              placeholder="Search models…"
            />
            {(selectedMfrs.length > 0 || selectedModels.length > 0) && (
              <button
                onClick={() => { setSelectedMfrs([]); setSelectedModels([]); setPage(1); }}
                className="text-white/70 hover:text-white text-sm underline ml-1"
                data-testid="firmware-clear-all-filters"
              >
                Clear filters
              </button>
            )}
            <div className="ml-auto">
              <Button
                onClick={handleExportCsv}
                disabled={exporting}
                className="bg-cyan-500 hover:bg-cyan-600 text-white text-sm"
                data-testid="firmware-export-csv-btn"
              >
                {exporting
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exporting…</>
                  : <><Download className="w-4 h-4 mr-2" />Export CSV</>}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="firmware-devices-table">
              <thead>
                <tr className="border-b border-[rgba(229,239,250,0.1)]">
                  <th className="text-left p-4 text-white/90 font-medium">Device Name</th>
                  <th className="text-left p-4 text-white/90 font-medium">Manufacturer</th>
                  <th className="text-left p-4 text-white/90 font-medium">Model</th>
                  <th className="text-left p-4 text-white/90 font-medium">Serial Number</th>
                  <th className="text-left p-4 text-white/90 font-medium">BIOS Version</th>
                  <th className="text-left p-4 text-white/90 font-medium">Latest Available</th>
                  <th className="text-left p-4 text-white/90 font-medium">Status</th>
                  <th className="text-left p-4 text-white/90 font-medium">TPM</th>
                  <th className="text-left p-4 text-white/90 font-medium">Download</th>
                  <th className="text-left p-4 text-white/90 font-medium">Deploy</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-white/70">
                      {loading ? 'Loading firmware data…' : 'No Windows devices match this filter.'}
                    </td>
                  </tr>
                ) : (
                  devices.map((d) => (
                    <tr
                      key={d.id}
                      className="border-b border-white/5 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]"
                      data-testid={`firmware-device-row-${d.id}`}
                    >
                      <td className="p-4 text-white">{d.device_name}</td>
                      <td className="p-4 text-white/90 text-sm">{d.manufacturer || '—'}</td>
                      <td className="p-4 text-white/90 text-sm">{d.model_family || d.model || '—'}</td>
                      <td
                        className="p-4 text-white/90 text-sm font-mono"
                        data-testid={`firmware-serial-${d.id}`}
                      >
                        {d.serial_number || <span className="text-white/40">—</span>}
                      </td>
                      <td className="p-4 text-white text-sm font-mono">
                        {d.bios_version || <span className="text-white/40">Unknown</span>}
                      </td>
                      <td className="p-4 text-white/90 text-sm font-mono">
                        {d.latest_bios_version
                          ? <span data-testid={`firmware-latest-${d.id}`}>{d.latest_bios_version}</span>
                          : <span className="text-white/40">—</span>}
                      </td>
                      <td className="p-4"><FirmwareStatusChip status={d.firmware_status} reason={d.unknown_reason} /></td>
                      <td className="p-4 text-white/90 text-sm">{d.tpm_version || '—'}</td>
                      <td className="p-4 text-sm">
                        {d.latest_bios_download_url ? (
                          <a
                            href={d.latest_bios_download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline"
                            data-testid={`firmware-download-${d.id}`}
                            title={
                              (d.manufacturer || '').toLowerCase().includes('microsoft')
                                ? 'Opens the Surface support page. The Microsoft .msi is a full driver+firmware bundle — packaging it for Intune is not recommended; ship Surface BIOS via Windows Update + Intune driver/firmware profiles instead.'
                                : d.latest_bios_download_url
                            }
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>BIOS</span>
                            {(d.manufacturer || '').toLowerCase().includes('microsoft') && (
                              <AlertTriangle
                                className="w-3 h-3 text-amber-400"
                                data-testid={`firmware-download-warn-${d.id}`}
                              />
                            )}
                            <ExternalLink className="w-3 h-3 opacity-70" />
                          </a>
                        ) : <span className="text-white/40">—</span>}
                      </td>
                      <td className="p-4 text-sm">
                        {canWriteOSPatch && ['missing_one', 'missing_multiple'].includes(d.firmware_status) && d.latest_bios_download_url ? (
                          <Button
                            size="sm"
                            onClick={() => setDeployWizardSelection({
                              manufacturer: d.manufacturer,
                              model_family: d.model_family || d.model,
                              target_version: d.latest_bios_version,
                              latest_download_url: d.latest_bios_download_url,
                              per_device: true,
                              device_id: d.id,
                              device_name: d.device_name,
                            })}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white h-7 px-2.5 text-xs"
                            data-testid={`firmware-package-deploy-${d.id}`}
                            title={`Package BIOS ${d.latest_bios_version} for ${d.device_name} and deploy via Intune Win32 app`}
                          >
                            <Rocket className="w-3 h-3 mr-1" />
                            Package & Deploy
                          </Button>
                        ) : (
                          <span className="text-white/40 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            currentPage={page}
            totalItems={
              devicesTotal
              ?? (summary
                ? (statusFilter
                    ? (summary.counts?.[statusFilter] || 0)
                    : summary.total_windows_devices)
                : devices.length)
            }
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      )}
      {canWriteOSPatch && deployWizardSelection && (
        <FirmwareDeploymentWizard
          initialSelection={deployWizardSelection}
          onClose={() => setDeployWizardSelection(null)}
          onCreated={() => {
            setDeployWizardSelection(null);
            toast.success('Firmware deployment queued — track progress under Deployments → Firmware Deployments.');
          }}
        />
      )}
    </Card>
  );
}
